import { useEffect, useRef } from "react";

const API_BASE_URL = import.meta.env.VITE_DOMAIN_TRACCAR || "";

const getWsUrl = () => {
  if (API_BASE_URL) {
    const wsUrl = API_BASE_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
    return `${wsUrl}/api/socket`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/socket`;
};

const scheduleIdle = typeof requestIdleCallback !== 'undefined'
  ? (fn) => requestIdleCallback(fn, { timeout: 500 })
  : (fn) => setTimeout(fn, 50);

const cancelIdle = typeof cancelIdleCallback !== 'undefined'
  ? cancelIdleCallback
  : clearTimeout;

export function useTraccarSocket(email, password, onMessage) {
  const wsRef        = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const pendingRef    = useRef([]);
  const flushIdRef    = useRef(null);  
  const flushTimerRef = useRef(null);  

  useEffect(() => {
    if (!email && !password) return;

    let closedByUser = false;
    let retry        = 0;
    const backoff    = () => Math.min(15000, 1000 * Math.pow(2, retry));

    const flush = () => {
      flushIdRef.current    = null;
      flushTimerRef.current = null;

      const batch = pendingRef.current;
      if (!batch.length) return;
      pendingRef.current = [];

      const devicesMap   = new Map();
      const positionsMap = new Map();

      for (const data of batch) {
        if (data.devices) {
          const arr = Array.isArray(data.devices) ? data.devices : [data.devices];
          arr.forEach(d => devicesMap.set(d.id, d));
        }
        if (data.device) {
          devicesMap.set(data.device.id, data.device);
        }
        if (data.positions) {
          const arr = Array.isArray(data.positions) ? data.positions : [data.positions];
          arr.forEach(p => {
            const prev  = positionsMap.get(p.deviceId);
            const tPrev = prev  ? new Date(prev.fixTime  || prev.deviceTime  || prev.serverTime  || 0).getTime() : -1;
            const tNow  =         new Date(p.fixTime     || p.deviceTime     || p.serverTime     || 0).getTime();
            if (tNow >= tPrev) positionsMap.set(p.deviceId, p);
          });
        }
        if (data.position) {
          const p     = data.position;
          const prev  = positionsMap.get(p.deviceId);
          const tPrev = prev ? new Date(prev.fixTime || prev.deviceTime || prev.serverTime || 0).getTime() : -1;
          const tNow  =        new Date(p.fixTime    || p.deviceTime    || p.serverTime    || 0).getTime();
          if (tNow >= tPrev) positionsMap.set(p.deviceId, p);
        }
      }

      const result = {};
      if (devicesMap.size)   result.devices   = Array.from(devicesMap.values());
      if (positionsMap.size) result.positions = Array.from(positionsMap.values());

      if (Object.keys(result).length > 0) {
        onMessageRef.current?.(result);
      }
    };

    const scheduleFlush = () => {
      if (flushIdRef.current || flushTimerRef.current) return;

      flushIdRef.current = scheduleIdle(flush);

      flushTimerRef.current = setTimeout(() => {
        if (flushIdRef.current) cancelIdle(flushIdRef.current);
        flushIdRef.current = null;
        flush();
      }, 600);
    };

    const handleMessage = (evt) => {
      let data;
      try { data = JSON.parse(evt.data); } catch { return; }

      if (!data || data.type === "ping") return;
      if (!data.devices && !data.device && !data.positions && !data.position) return;

      pendingRef.current.push(data);
      scheduleFlush();
    };

    const connect = () => {
      const WS_URL = getWsUrl();
      if (import.meta.env.DEV) console.log("[Traccar WS] 🔌 Connecting to:", WS_URL);
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        if (import.meta.env.DEV) console.log("[Traccar WS] ✅ CONNECTED");
        retry = 0;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (e) => { if (import.meta.env.DEV) console.error("[Traccar WS] ❌ ERROR:", e); };

      ws.onclose = (evt) => {
        if (import.meta.env.DEV) console.warn("[Traccar WS] 🔌 CLOSED:", evt.code);
        wsRef.current = null;
        if (!closedByUser && evt.code !== 1000) {
          retry += 1;
          setTimeout(connect, backoff());
        }
      };

      wsRef.current = ws;
    };

    const timer = setTimeout(connect, 1000);

    return () => {
      closedByUser = true;
      clearTimeout(timer);

      if (flushIdRef.current)    { cancelIdle(flushIdRef.current);        flushIdRef.current    = null; }
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current);   flushTimerRef.current = null; }
      pendingRef.current = [];

      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [email, password]);

  return wsRef;
}
